
import React, { createContext } from 'react';
import { initializeApp, getApp, getApps } from "firebase/app";
import { getDatabase, ref, query, orderByChild, equalTo, startAt, endAt, limitToLast, limitToFirst } from "firebase/database";
// Re-exported below for the mobile app (see the export list): its screens used to import
// these from "firebase/database" / "firebase/auth" directly, which resolved to a SECOND copy
// of the SDK in the bundle (PERFORMANCE_AUDIT P2-5).
import { onValue, off, get, child, goOnline } from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";
import { initializeAuth, getAuth, GoogleAuthProvider, OAuthProvider, signInWithPhoneNumber, PhoneAuthProvider, RecaptchaVerifier, unlink, updatePhoneNumber, linkWithPhoneNumber, browserLocalPersistence, browserPopupRedirectResolver } from "firebase/auth";
import { getStorage, ref as stRef } from "firebase/storage";

// Cap on the MOBILE bookings listener (customer / driver), applied only when
// a caller passes it as bookingListRef's 4th argument — bookinglistactions
// does for the app's live list. Those two queries were unbounded, so a
// veteran account re-downloaded its lifetime history at every start and
// again on every reconnect. Push ids are chronological, so limitToLast keeps
// the newest 300; the client (bookinglistactions) keeps a 7-day window on
// top of that anyway. The other users of the same ref (driver earnings,
// portal UserRides / DriverClearanceLetter, DonkeyMeterScreen) pass nothing
// and keep the full history.
const MOBILE_BOOKINGS_LIMIT = 300;

const FirebaseContext = createContext(null);

let firebase = {
    app: null,
    database: null,
    auth: null,
    storage: null,
}   

const createFullStructure = (app, db, auth, storage, config) => {
    return {
        app: app,
        config: config,
        database: db,
        auth: auth,
        storage: storage,
        authRef:getAuth,
        googleProvider:new GoogleAuthProvider(),
        appleProvider:new OAuthProvider('apple.com'),
        phoneProvider:new PhoneAuthProvider(auth),          
        RecaptchaVerifier: RecaptchaVerifier,
        signInWithPhoneNumber: signInWithPhoneNumber,   
        updatePhoneNumber:updatePhoneNumber,
        unlink: unlink,
        googleCredential: (idToken, accessToken)=> GoogleAuthProvider.credential(idToken, accessToken),    
        linkWithPhoneNumber: linkWithPhoneNumber,
        mobileAuthCredential: (verificationId,code) => PhoneAuthProvider.credential(verificationId, code),           
        usersRef: ref(db, 'users'),
        bookingRef: ref(db, 'bookings'),
        cancelreasonRef: ref(db, 'cancel_reason'),
        settingsRef: ref(db, 'settings'),
        smtpRef: ref(db, "smtpdata"),
        smsRef: ref(db, "smsConfig"),
        carTypesRef: ref(db, 'cartypes'),
        carTypesEditRef:(id) => ref(db, "cartypes/"+ id),
        carDocImage:(id) =>  stRef(storage, `cartypes/${id}`),
        // Live-event poster — path keyed by eventId so a new event's image
        // never serves a stale cached URL from the previous one.
        eventImageRef:(id) => stRef(storage, `event_images/${id}`),
        promoRef: ref(db, 'promos'),
        promoEditRef:(id) => ref(db, "promos/"+ id),
        notifyRef: ref(db,"notifications/"),
        notifyEditRef:(id) => ref(db, "notifications/"+ id),
        scheduledNotifyRef: ref(db,"scheduled_notifications/"),
        scheduledNotifyEditRef:(id) => ref(db, "scheduled_notifications/"+ id),
        // Bulk-delivery campaigns. Read-only for the portal (the server owns
        // every write) and deliberately narrow: /campaigns holds only small
        // aggregates, while the recipient slices and per-recipient ledgers stay
        // server-side so a browser never downloads them.
        campaignsRef: ref(db, "campaigns"),
        campaignRef:(id) => ref(db, "campaigns/"+ id),
        addressRef: (uid,id) =>  ref(db, "savedAddresses/"+ uid + "/" + id),
        addressEditRef:(uid) => ref(db, "savedAddresses/"+ uid),
        singleUserRef:(uid) => ref(db, "users/" + uid),
        profileImageRef:(uid) => stRef(storage,`users/${uid}/profileImage`),
        verifyIdImageRef:(uid) => stRef(storage,`users/${uid}/verifyIdImage`),
        insuranceImageRef:(uid) => stRef(storage,`users/${uid}/insuranceImage`),
        bookingImageRef:(bookingId,imageType) => stRef(storage,`bookings/${bookingId}/${imageType}`),
        driversRef: query(ref(db, "users"), orderByChild("usertype"), equalTo("driver")),
        // Slim projection of active drivers maintained by the
        // `rebuildActiveDriversSlim` Cloud Function. The customer mobile app
        // reads from THIS instead of `driversRef` so a single fetch is ~30 KB
        // instead of ~10 MB. See the Cloud Function in functions/index.js
        // for what fields are included.
        activeDriversSlimRef: ref(db, "active_drivers_slim"),
        eligibleDriversRef: ref(db, "eligible_drivers"),
        driverDocsRef:(uid) => stRef(storage,`users/${uid}/license`),       
        driverDocsRefBack:(uid) => stRef(storage,`users/${uid}/licenseBack`),         
        singleBookingRef:(bookingKey) => ref(db, "bookings/" + bookingKey),
        requestedDriversRef:(bookingKey ) => ref(db, "bookings/" + bookingKey  + "/requestedDrivers"),
        referralIdRef:(referralId) => query(ref(db, "users"), orderByChild("referralId"), equalTo(referralId)),
        trackingRef: (bookingId) => ref(db, 'tracking/' + bookingId),
        tasksRef:() => query(ref(db, 'bookings'), orderByChild('status'), equalTo('NEW')),
        // Per-driver task index maintained server-side by the
        // `onRequestedDriverChanged` and `onBookingStatusChanged` Cloud
        // Functions. The driver mobile app listens to ONLY their own slice
        // here instead of subscribing to all NEW bookings system-wide.
        // Single biggest driver-side RTDB cost reduction.
        driverTasksRef: (uid) => ref(db, `driver_tasks/${uid}`),
        singleTaskRef:(uid,bookingId) => ref(db, "bookings/" + bookingId  + "/requestedDrivers/" + uid),
        // Admin/callcenter/finance/scout previously fell through to the
        // unscoped /bookings root — every page load downloaded the entire
        // collection, and the onValue listener re-downloaded it on every
        // booking write anywhere in the system. fromTs (epoch ms) bounds
        // those roles to bookings since that timestamp via the existing
        // 'tripdate' index on /bookings. Drivers/customers/fleetadmin keep
        // their existing scoped queries; `limit` (see MOBILE_BOOKINGS_LIMIT)
        // caps the customer/driver ones to the newest N for the app's list.
        bookingListRef:(uid, role, fromTs, limit) =>
            role == 'customer'? (limit ? query(ref(db, 'bookings'), orderByChild('customer'), equalTo(uid), limitToLast(limit)) : query(ref(db, 'bookings'), orderByChild('customer'), equalTo(uid))):
                (role == 'driver'?
                    (limit ? query(ref(db, 'bookings'), orderByChild('driver'), equalTo(uid), limitToLast(limit)) : query(ref(db, 'bookings'), orderByChild('driver'), equalTo(uid)))
                    :
                    (role == 'fleetadmin'?
                        query(ref(db, 'bookings'), orderByChild('fleetadmin'), equalTo(uid))
                        : query(ref(db, 'bookings'), orderByChild('tripdate'), startAt(fromTs || (Date.now() - 30 * 86400000)))
                    )
                ),
        // Bookings whose tripdate falls in [fromTs, toTs] — one indexed range
        // query (tripdate is in bookings .indexOn) for date-bounded reports such
        // as the Ethiopian yearly-tax tab. Mirrors the admin tripdate query above
        // but bounds the upper end with endAt.
        bookingsByDateRangeRef:(fromTs, toTs) =>
            query(ref(db, 'bookings'), orderByChild('tripdate'), startAt(fromTs), endAt(toTs)),
        // Same two admin/report queries on the server-stamped numeric
        // `tripdateMs` mirror (stampTripdateMs Cloud Function). `tripdate` is
        // mixed-type — epoch-ms numbers from regular bookings, ISO strings from
        // the Donkey Meter / offline paths — and RTDB orders numbers before
        // strings, so a numeric startAt on it silently pulls every string-dated
        // booking ever created and a numeric endAt excludes them all.
        // tripdateMs is always a number. Selected by settings.useTripdateMsIndex
        // (portal admin-scope roles only); the refs above stay the default.
        bookingListByMsRef:(fromTs) =>
            query(ref(db, 'bookings'), orderByChild('tripdateMs'), startAt(fromTs || (Date.now() - 30 * 86400000))),
        bookingsByDateRangeMsRef:(fromTs, toTs) =>
            query(ref(db, 'bookings'), orderByChild('tripdateMs'), startAt(fromTs), endAt(toTs)),
        // Persisted per-year aggregate for the Ethiopian yearly-tax tab. Generated
        // on demand (heavy bookings scan), then saved here so re-opening the tab is
        // one slim read instead of re-scanning a whole year of bookings.
        yearlyTaxReportRef:(year) => ref(db, 'yearly_tax_reports/' + year),
        chatRef:(bookingId) => ref(db, 'chats/' + bookingId + '/messages'),
        withdrawRef: ref(db, 'withdraws/'),
        // Bounded /withdraws reads for the portal (settings.withdrawFetchBoundDays):
        // every row still marked processed === false (pending — also matches
        // rejected/reversed, an acceptable superset) plus everything requested
        // since fromTs. Both fields are in the withdraws .indexOn.
        withdrawsUnprocessedRef: query(ref(db, 'withdraws'), orderByChild('processed'), equalTo(false)),
        withdrawsSinceRef:(fromTs) => query(ref(db, 'withdraws'), orderByChild('date'), startAt(fromTs)),
        languagesRef: ref(db, "languages"),
        languagesEditRef:(id) => ref(db, "languages/"+ id),
        langEditRef:(id) => ref(db, `languages/${id}/keyValuePairs/`),
        // Language index (~1 KB, server-maintained, functions/common/languagesMeta.js)
        // and one pack: the apps load the default + the user's language only.
        languagesMetaRef: ref(db, 'languages_meta'),
        languagePackRef: (id) => ref(db, 'languages/' + id + '/keyValuePairs'),
        walletHistoryRef:(uid) => ref(db, "walletHistory/" + uid),  
        userNotificationsRef:(uid) =>  ref(db, "userNotifications/"+ uid),
        donkeyMilesHistoryRef:(uid) =>  ref(db, "donkeyMilesHistory/"+ uid),
        userRatingsRef:(uid) =>  ref(db, "userRatings/"+ uid),
        carsRef:(uid,role) => role == 'driver'? 
            query(ref(db, 'cars'), orderByChild('driver'), equalTo(uid))
            :(role == 'fleetadmin'? 
                query(ref(db, 'cars'), orderByChild('fleetadmin'), equalTo(uid))
                : ref(db, 'cars')
            ),
        carAddRef: ref(db, "cars"),
        carEditRef:(id) => ref(db, "cars/"+ id),
        // Just one driver's cars (indexed on 'driver') — for the per-user
        // refresh button on the driver detail page (cheap, not the full /cars).
        carsByDriverRef:(driverId) => query(ref(db, 'cars'), orderByChild('driver'), equalTo(driverId)),
        carImage:(id) => stRef(storage,`cars/${id}`),   
        allLocationsRef: ref(db, "locations"),
        // Bounded /locations read for the portal: only rows whose `updateTime`
        // (server-stamped epoch ms, in the /locations .indexOn) is at/after
        // sinceMs. Most of the ~7k rows are months stale, so the 24 h window the
        // users/locations join and the Dashboard live map pass is a few KB
        // instead of the full 0.65 MB node. Web-only callers; allLocationsRef
        // above is unchanged.
        recentLocationsRef:(sinceMs) => query(ref(db, "locations"), orderByChild("updateTime"), startAt(sinceMs)),
        userLocationRef:(uid) => ref(db, "locations/"+ uid),
        userContactsRef:(uid) => ref(db, "contacts/"+ uid),
        allContactsRef: ref(db, "contacts"),
        sosRef: ref(db, 'sos'),
        // Date-bounded /sos query for admin pages — uses 'complainDate'
        // index. Falls back to bare ref if no fromTs supplied.
        sosByDateRef: (fromTs) => fromTs
            ? query(ref(db, 'sos'), orderByChild('complainDate'), startAt(fromTs))
            : ref(db, 'sos'),
        editSosRef:(id) => ref(db, "sos/" +id),
        complainRef: ref(db, 'complain'),
        // Date-bounded /complain query for admin pages — uses 'complainDate'
        // index (matches the field both SOS and complain records carry,
        // see discordNotifications.js). Falls back to bare ref if no fromTs.
        complainByDateRef: (fromTs) => fromTs
            ? query(ref(db, 'complain'), orderByChild('complainDate'), startAt(fromTs))
            : ref(db, 'complain'),
        editComplainRef:(id) => ref(db, "complain/" +id),
        // Audit log of every driver letter generated from the dashboard
        // (verification + clearance) — one slim record per generation so staff
        // can later double-check an issued physical letter against fraud.
        driverLettersRef: ref(db, 'driver_letters'),
        // Server-side filter by usertype — for pages that only need
        // customers OR drivers, not the entire /users tree.
        usersByTypeRef: (usertype) => usertype
            ? query(ref(db, 'users'), orderByChild('usertype'), equalTo(usertype))
            : ref(db, 'users'),
        // Slim, customers-ONLY projection maintained by the
        // `maintainCustomerIndex` Cloud Function (mirrors active_drivers_slim).
        // The Riders customer list paginates THIS node instead of loading the
        // entire ~45k customer base out of /users. Indexed on
        // [createdAt, hasInvitedContacts, appLess, mobile, searchName].
        customerIndexRef: ref(db, 'customer_index'),
        // One page of the newest customers, ordered by createdAt. We fetch
        // pageSize+1 so the extra row signals "hasMore" and gives the next
        // cursor; the client reverses the slice to newest-first. With a cursor
        // (the oldest createdAt seen so far) it pulls the next older page.
        customersPageRef: (cursor, pageSize) => cursor
            ? query(ref(db, 'customer_index'), orderByChild('createdAt'), endAt(cursor), limitToLast((pageSize || 25) + 1))
            : query(ref(db, 'customer_index'), orderByChild('createdAt'), limitToLast((pageSize || 25) + 1)),
        // Backend lookups for the Riders filters/search — each is a single
        // indexed query over the customers-only index (sorted client-side by
        // createdAt since RTDB allows only one orderByChild per query).
        customersByInvitedRef: (flag, limit) => limit
            ? query(ref(db, 'customer_index'), orderByChild('hasInvitedContacts'), equalTo(flag === true), limitToFirst(limit))
            : query(ref(db, 'customer_index'), orderByChild('hasInvitedContacts'), equalTo(flag === true)),
        customersAppLessRef: (limit) => limit
            ? query(ref(db, 'customer_index'), orderByChild('appLess'), equalTo(true), limitToFirst(limit))
            : query(ref(db, 'customer_index'), orderByChild('appLess'), equalTo(true)),
        customersByMobileRef: (mobile) => query(ref(db, 'customer_index'), orderByChild('mobile'), equalTo(mobile)),
        customersByNameRef: (prefix, limit) => query(ref(db, 'customer_index'), orderByChild('searchName'), startAt(prefix), endAt(prefix + ''), limitToFirst(limit || 25)),
        // Cheap aggregate counters maintained by the same CF — feeds the
        // Dashboard customer tiles and the Riders Invitation Overview without
        // counting the full customer base.
        customerIndexStatsRef: ref(db, 'customer_index_stats'),
        dashboardStatsRef: ref(db, 'dashboard_stats'),
        // Driver "Hot zones" snapshot written by publishDemandHeat every 10 min
        // (wire format: common/src/other/demandHeatCodec.js). The phone reads
        // EXACT child paths at different cadences: the ambient chip needs only
        // the ~0.75 KB summary `s`; the sheet / Demand view add the cell strings
        // `f` (~5 KB) through a second exact-path read pipelined with `s`, so a
        // driver never downloads `_meta` (or anything else under /demand_heat).
        // One-shot get() only — never a listener — so the RTDB bill does not
        // move with driver count. `demandHeatRef` (the whole node, `_meta`
        // included) is for portal / ops tooling only; phones must not use it.
        demandHeatRef: ref(db, 'demand_heat'),
        // City events (crowd events the call centre publishes for drivers):
        // one-shot queries on the list, a day index for the schedulers, the
        // poster in Storage; dbRootRef for the multi-path event + index write.
        cityEventsRef: ref(db, 'city_events'),
        cityEventRef: (id) => ref(db, 'city_events/' + id),
        cityEventsIndexRef: ref(db, 'city_events_index'),
        cityEventSourcesRef: ref(db, 'city_events_sources'),
        cityEventSourceRef: (id) => ref(db, 'city_events_sources/' + id),
        cityEventImageRef: (id) => stRef(storage, 'city_events/' + id + '/poster'),
        cityEventsImpactMetaRef: ref(db, 'city_events_impact/_meta'),
        // City events for riders: the publisher's tiny public projection (the
        // next events only — never /city_events itself). Rider phones read ONLY
        // this exact child, with a one-shot get() on a TTL
        // (mobile-app/src/services/cityEventsRiderCache.js), never a listener.
        cityEventsRiderRef: ref(db, 'city_events_rider/s'),
        // Driver ringtones for the incoming-booking alert: admins upload short
        // clips in Settings › Ringtones; a driver's phone reads the list once
        // when the picker opens and caches the chosen file on the device.
        ringtonesRef: ref(db, 'ringtones'),
        ringtoneRef: (id) => ref(db, 'ringtones/' + id),
        ringtoneDefaultRef: ref(db, 'ringtone_default'),
        ringtoneFileRef: (id) => stRef(storage, 'ringtones/' + id),
        dbRootRef: ref(db),
        demandHeatSummaryRef: ref(db, 'demand_heat/s'),
        demandHeatCellsRef: ref(db, 'demand_heat/f'),
        // Publisher health counters (~0.3 KB) — portal admin health line only.
        demandHeatMetaRef: ref(db, 'demand_heat/_meta'),
        // CF-maintained monthly earnings rollups (functions/common/reportRollups.js):
        // system_monthly/{YYYY-MM}, driver_monthly/{YYYY-MM}/{driverUid},
        // fleet_monthly/{YYYY-MM}/{fleetadminUid}. Month keys are zero-padded
        // Addis-time months. Portal-only reads behind settings.useReportRollups;
        // the legacy full-/bookings scans in the earnings actions stay the default.
        reportRollupSystemRef: ref(db, 'report_rollups/system_monthly'),
        reportRollupDriverRef: ref(db, 'report_rollups/driver_monthly'),
        reportRollupFleetRef: ref(db, 'report_rollups/fleet_monthly'),
        paymentSettingsRef: ref(db, "payment_settings"),
        // Live-event stream URL — admin-only node, deliberately NOT under the
        // world-readable /settings so unpaid users can't lift the URL.
        eventSettingsRef: ref(db, "event_settings"),
        // Paid entitlements written by the payment callback; portal-only
        // reads (admin/finance/callcentersupervisor per DB rules).
        eventPaymentsRef: ref(db, "event_payments"),
        // Curated landmark database (populated by import_addis_landmarks
        // Cloud Function from Google Places). Mobile RiderSearch and web
        // AddBookings use this to surface "Bambis" / "Adams Pavilion" /
        // etc. above generic Google Places autocomplete.
        landmarksRef: ref(db, 'landmarks'),
        landmarksByCategoryRef: (category) => category
            ? query(ref(db, 'landmarks'), orderByChild('category'), equalTo(category))
            : ref(db, 'landmarks'),
        usedreferralRef:ref(db,'usedreferral'),
        otpAuthByMobileRef:(mobile) => query(ref(db, "otp_auth_requests"), orderByChild("mobile"), equalTo(mobile)),
        driverRemarksRef: ref(db, 'driver_remarks'),
        driverRemarksByDriverRef:(driverId) => query(ref(db, "driver_remarks"), orderByChild("driverId"), equalTo(driverId)),
        // Remarks stamped on/after an ISO timestamp (the only writer stamps
        // toISOString; 'timestamp' is indexed). The agent-performance pages pass
        // a floor so the full /driver_remarks tree is never downloaded.
        driverRemarksSinceRef:(fromIso) => query(ref(db, "driver_remarks"), orderByChild("timestamp"), startAt(fromIso)),
        editDriverRemarkRef:(id) => ref(db, "driver_remarks/" + id),
        serviceOutagesRef: ref(db, 'service_outages'),
        // Donkey Mile redemption. The card pool itself is admin-read /
        // server-write only; clients read the tiny counters node for stock
        // and the portal loads one provider+denomination subtree on demand.
        phoneCardsMetaRef: ref(db, 'phone_cards_meta'),
        phoneCardsPoolRef: (provider, value) => ref(db, 'phone_cards/' + provider + '/' + value),
        ngosRef: ref(db, 'ngos'),
        ngoEditRef: (id) => ref(db, 'ngos/' + id),
        // Server-only donation tallies per NGO (miles, donationCount,
        // etbValue, lastDonationAt) — tiny node, safe one-shot read.
        ngoStatsRef: ref(db, 'ngo_stats'),
        // Individual donation ledger (admin/finance read). Date-bounded via
        // the existing .indexOn ["date"] so the portal never pulls the full
        // history by default.
        ngoDonationsByDateRef: (fromTs) => fromTs
            ? query(ref(db, 'ngo_donations'), orderByChild('date'), startAt(fromTs))
            : ref(db, 'ngo_donations'),
        ngoImageRef: (id) => stRef(storage, `ngos/${id}`),
        // Airtime provider logos (ethiotel / safaricom), uploaded from the
        // Donkey Miles settings tab; URLs live in settings.airtimeProviderIcons.
        providerIconRef: (key) => stRef(storage, `providers/${key}`),
    }
}

// True on React Native, false in the browser. The shortened upload retry
// window below is for the mobile onboarding wizard only; web admin uploads
// (car type icons, NGO logos, event posters) keep the SDK default.
const isReactNative = () =>
    typeof document === 'undefined' ||
    (typeof navigator !== 'undefined' && navigator.product === 'ReactNative');

const FirebaseProvider  = ({ config, children, AsyncStorage, token }) => {
    let app, auth, database, storage;

    if (!getApps().length) {
        try {
            app = initializeApp(config);

            if (typeof document !== 'undefined') {
                // Web environment
                auth = initializeAuth(app, {
                    persistence: browserLocalPersistence,
                    popupRedirectResolver: browserPopupRedirectResolver,
                });
            }
            else{
                // React Native environment
                if (AsyncStorage) {
                    
                    const { getReactNativePersistence } = require("firebase/auth");

                    auth = initializeAuth(app, {
                        persistence: getReactNativePersistence(AsyncStorage)
                    });
                } else {
                    // Fallback to default auth if AsyncStorage is not provided
                    console.warn("AsyncStorage not provided, auth state will not persist between sessions");
                    auth = getAuth(app);
                }
            }
            database = getDatabase(app);
            storage = getStorage(app);
            // RN only: Firebase default is 10 min of silent retries; the onboarding wizard shows an error after 90 s instead of spinning.
            if (isReactNative()) {
                try { storage.maxUploadRetryTime = 90000; } catch (e) { /* non-fatal */ }
            }
        } catch (error) {
            console.log("Error initializing app: " + error);
        }
    } else {
        app = getApp();
        auth = getAuth(app);
        database = getDatabase(app);
        storage = getStorage(app);
        // RN only: Firebase default is 10 min of silent retries; the onboarding wizard shows an error after 90 s instead of spinning.
        if (isReactNative()) {
            try { storage.maxUploadRetryTime = 90000; } catch (e) { /* non-fatal */ }
        }
    }

    firebase = createFullStructure(app, database, auth, storage, config);

    return (
        <FirebaseContext.Provider value={firebase}>
            {children}
        </FirebaseContext.Provider>
    )
}

export {
    firebase,
    FirebaseProvider,
    FirebaseContext,
    MOBILE_BOOKINGS_LIMIT,
    // the SDK helpers the mobile app needs, from this one copy of the SDK
    ref, query, orderByChild, equalTo, onValue, off, get, child, goOnline, onAuthStateChanged
}